import React, { useContext } from "react";
import useOnlineStatus from "../hooks/useOnlineStatus";
import { Redirect, Route, Switch } from "react-router-dom";
import { useFeatureFlags } from "@evidenceb/athena-common/modules/FeatureFlags";
import { getUrl, shouldChooseSubject } from "../utils/dataRetrieval";
import { useUserExtra } from "../hooks/queries/useUserExtra";
import { configStore } from "../contexts/ConfigContext";
import { UserType } from "../interfaces/User";
import { isAuthorized } from "./utils";
import { Pagetype } from "../interfaces/Config";
import TeacherHomeResolver from "../pages/Home/TeacherHomeResolver";
import TeacherModuleList from "../pages/TeacherModuleList/TeacherModuleList";
import SubjectChoiceRouter from "../pages/SubjectChoice/SubjectChoiceRouter";
import PedagogicalResourcesManagement from "../pages/PedagogicalResourcesManagement/PedagogicalResourcesManagement";
import DashboardRouter from "../pages/DashBoard/DashboardTeacher/DashboardRouter";
import WorkModeSelection from "../pages/Workmodes/Selection/WorkModeSelection";
import ContentPage from "../pages/ContentPage/ContentPage";
import TeacherDuoWorkMode from "../pages/Workmodes/Duo/Teacher/TeacherDuoWorkMode";
import TutoMode from "../pages/Workmodes/Tuto/Router";
import TeacherWorkshopRouter from "../pages/Workmodes/Workshop/Teacher/Router";
import TeacherPlayer from "../pages/Workmodes/SoloAI/Teacher/Player/TeacherPlayer";
import { withLoader } from "../utils/withLoader";
import TeacherPlaylistWorkModeRouter from "../pages/Workmodes/Playlist/Teacher/TeacherPlaylistWorkModeRouter";
import SingleExercisePlayer from "../pages/DashBoard/Common/SingleExercisePlayer/SingleExercisePlayer";
import TeacherExperimentationWorkModeRouter from "../pages/Workmodes/Experimentation/Teacher/TeacherExperimentationWorkModeRouter";
import { useSubjects } from "../hooks/useActiveSubject";
import { sessionStore } from "../contexts/SessionContext";
import { useStudentsAlerts } from "../pages/DashBoard/DashboardTeacher/ProgressionOverview/useStudentsAlerts";
import { isSpecimenVersion } from "../utils/init";
import UserTypeSwitch from "../components/Header/UserTypeSwitch/UserTypeSwitch";
import { useBreakpoints } from "@evidenceb/athena-common/helpers/breakpoints";

const TeacherRouter = () => {
    const isOnline = useOnlineStatus();

    const featureFlags = useFeatureFlags();
    const [extra] = useUserExtra();
    const subjects = useSubjects();

    const isSubjectChoiceNeeded = shouldChooseSubject(
        featureFlags,
        extra.activeSubject,
        subjects
    );

    return (
        <Switch>
            <Route path="/subject-choice">
                <SubjectChoiceRouter />
            </Route>

            {isSubjectChoiceNeeded && <Redirect to="/subject-choice" />}

            <Route path="/player/:moduleId?/:objectiveId?">
                <TeacherPlayer />
            </Route>
            {/* [TEMP] For now the timestamp does not exist in the teacher dashboard, so
            we rely on occurrence, which is buggy because playlist results might appear in the midst.
            Definite implementation should only rely on timestamp
            See https://evidencebprod.atlassian.net/browse/EP-2785 for resolution status & more info */}
            <Route path="/play/:exerciseId/:studentId?/:exerciseOccurrenceOrTimestamp?">
                <SingleExercisePlayer />
            </Route>

            {isOnline && (
                <Route path="/prm/:classroomId">
                    <PedagogicalResourcesManagement />
                </Route>
            )}

            <Route>
                <TeacherPagesRouter />
            </Route>
        </Switch>
    );
};
export default withLoader(TeacherRouter);

const TeacherPagesRouter = () => {
    const { config } = useContext(configStore);
    const {
        session: { classrooms },
    } = useContext(sessionStore);
    const featureFlags = useFeatureFlags();
    const [extra] = useUserExtra();
    const subjects = useSubjects();

    const isSubjectChoiceNeeded = shouldChooseSubject(
        featureFlags,
        extra.activeSubject,
        subjects
    );
    useStudentsAlerts(
        classrooms ? classrooms.map((classroom) => classroom.id) : []
    );
    const { isMobile } = useBreakpoints();
    return (
        <div className="content">
            <Switch>
                {config.pages.map((page) => {
                    if (
                        isSubjectChoiceNeeded ||
                        (!isAuthorized(UserType.Teacher, page) &&
                            !page.fallback)
                    )
                        return null;

                    const pageType = page.type;
                    const pageUrl = `/${getUrl(page, UserType.Teacher)}`;
                    switch (pageType) {
                        case Pagetype.HOME:
                            return (
                                <Route key={pageType} path={pageUrl} exact>
                                    <TeacherHomeResolver />
                                </Route>
                            );

                        case Pagetype.MODULELIST:
                            return (
                                <Route key={pageType} path={pageUrl}>
                                    <TeacherModuleList />
                                </Route>
                            );

                        case Pagetype.DASHBOARD:
                            return (
                                <Route key={pageType} path={pageUrl}>
                                    <DashboardRouter />
                                </Route>
                            );

                        case Pagetype.WORK_MODES:
                            return (
                                <Route key={pageType} path={pageUrl}>
                                    <WorkModeSelection />
                                </Route>
                            );

                        case Pagetype.CONTENTPAGE:
                            return (
                                <Route key={pageType} path={pageUrl}>
                                    <ContentPage />
                                </Route>
                            );

                        case Pagetype.WORKMODE_DUO:
                            return (
                                <Route key={pageType} path={pageUrl}>
                                    <TeacherDuoWorkMode />
                                </Route>
                            );

                        case Pagetype.WORKMODE_PLAYLIST:
                            return (
                                <Route key={pageType} path={pageUrl}>
                                    <TeacherPlaylistWorkModeRouter />
                                </Route>
                            );

                        case Pagetype.WORKMODE_TUTO: {
                            return (
                                <Route key={pageType} path={pageUrl}>
                                    <TutoMode />
                                </Route>
                            );
                        }

                        case Pagetype.WORKMODE_WORKSHOP:
                            return (
                                <Route key={pageType} path={pageUrl}>
                                    <TeacherWorkshopRouter />
                                </Route>
                            );
                        case Pagetype.WORKMODE_EXPERIMENTATION:
                            return (
                                <Route key={pageType} path={pageUrl}>
                                    <TeacherExperimentationWorkModeRouter />
                                </Route>
                            );

                        default:
                            console.info(
                                `No teacher page for pageType ${pageType}`
                            );
                            return null;
                    }
                })}
            </Switch>
            {isMobile && isSpecimenVersion() && (
                <UserTypeSwitch className="header__user-switch" />
            )}
        </div>
    );
};
